import React from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";

import Config from "./../../Config";
import {
  setAddress,
  set_OfficeAddress,
} from "../../../store/Actions/authActions";
import OnboardingLayout, { Icon } from "../OnboardingLayout";

/**
 * Onboarding 02a — adresse de collecte et livraison. Le même écran sert
 * à créer une adresse pendant l'onboarding et à en ajouter ou modifier
 * une depuis le menu (« ajouter une adresse »), d'où les trois actions.
 */
class DeliveryDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      uid: null,
      client: null,
      accessToken: null,
      street: "",
      nr: "",
      zip_code: "",
      city: "",
      country: "Belgium",
      comment: "",
      loading: false,
      success: false,
      error: false,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.setState({
      uid: this.props?.authUser?.user_uid,
      client: this.props?.authUser?.user_client,
      accessToken: this.props?.authUser?.user_access_token,
    });

    if (this.isEditing) {
      this.fillFromEditable();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.isEditing && this.props !== prevProps) {
      this.fillFromEditable();
    }
  }

  get isEditing() {
    return Boolean(this.props.location?.state?.edit);
  }

  /** `myLove` marque le passage par l'inscription : il pilote la jauge d'étapes. */
  get onboarding() {
    return this.props.location?.state?.myLove === "Pakistan";
  }

  get editable() {
    return this.props.location?.state?.editable || {};
  }

  fillFromEditable() {
    const editable = this.editable;

    this.setState({
      street: editable.street || "",
      nr: editable.nr || "",
      zip_code: editable.zip_code || "",
      city: editable.city || "",
      country: editable.country || "Belgium",
      comment: editable.comment || "",
    });
  }

  get headers() {
    return {
      uid: `${this.state.uid}`,
      client: `${this.state.client}`,
      "access-token": `${this.state.accessToken}`,
    };
  }

  get payload() {
    return {
      delivery_address: {
        street: this.state.street,
        zip_code: this.state.zip_code,
        country: this.state.country,
        nr: this.state.nr,
        comment: this.state.comment,
        city: this.state.city,
      },
    };
  }

  get isComplete() {
    const { street, city, zip_code, nr, country } = this.state;

    return Boolean(street && city && zip_code && nr && country);
  }

  handleChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleBack = () => {
    this.props.history.push(this.isEditing ? "/home" : "/use-wosh", {
      myLove: this.props.location?.state?.myLove,
    });
  };

  refreshAddresses() {
    this.props?.dispatch(setAddress());
    this.props?.dispatch(set_OfficeAddress());
  }

  handleSubmit = async () => {
    this.setState({ loading: true });

    await Axios({
      url: `${Config.apiURL}/customer/delivery_addresses`,
      method: "POST",
      data: this.payload,
      headers: this.headers,
    })
      .then(() => {
        this.setState({ loading: false, success: true });
        this.refreshAddresses();
        this.props.history.push("/thank-you", {
          myLove: this.props.location?.state?.myLove,
        });
      })
      .catch(() => {
        this.setState({ error: true, loading: false });
      });
  };

  handleEdit = async (id) => {
    this.setState({ loading: true });

    await Axios({
      url: `${Config.apiURL}/customer/delivery_addresses/${id}`,
      method: "PUT",
      data: this.payload,
      headers: this.headers,
    })
      .then(() => {
        this.setState({ loading: false, success: true });
        this.refreshAddresses();
        this.props?.history.push("/home");
      })
      .catch(() => {
        this.setState({ error: true, loading: false });
      });
  };

  handleDefaultAddress = async (type, id) =>
    Axios({
      url: `${Config.apiURL}/customer/default_addresses`,
      method: "POST",
      headers: this.headers,
      data: { id, address_type: type },
    });

  handleDelete = async (id) => {
    this.setState({ loading: true });

    try {
      await Axios({
        method: "DELETE",
        url: `${Config.apiURL}/customer/delivery_addresses/${id}`,
        headers: this.headers,
      });

      /*
       * Supprimer l'adresse par défaut laisserait le compte sans adresse
       * de référence : on en promeut une autre, adresse domicile d'abord,
       * point de collecte ensuite.
       */
      const removed = this.props?.homeadd?.filter((item) => item.id === id);

      if (removed?.[0]?.is_default === true) {
        const remaining = this.props?.homeadd?.filter((item) => item.id !== id);

        if (remaining?.length > 0) {
          await this.handleDefaultAddress("DeliveryAddress", remaining[0]?.id);
        } else if (this.props?.offadd?.length > 0) {
          await this.handleDefaultAddress(
            "CollectionPoint",
            this.props.offadd[0]?.id
          );
        }
      }

      this.refreshAddresses();
      this.props.history.push("/home");
      this.setState({ loading: false });
    } catch (err) {
      this.setState({
        errorMessage: err?.response?.data?.message?.delivery_address?.[0],
        loading: false,
      });
    }
  };

  renderField(name, { label, placeholder, icon, optional, type = "text" }) {
    return (
      <div>
        <label className="wosh-ob__label" htmlFor={`address-${name}`}>
          {label}
          {optional && <span className="wosh-ob__optional">{optional}</span>}
        </label>
        <div className="wosh-ob__field">
          {icon && <Icon name={icon} />}
          <input
            id={`address-${name}`}
            name={name}
            type={type}
            placeholder={placeholder}
            value={this.state[name]}
            onChange={this.handleChange}
          />
        </div>
      </div>
    );
  }

  /**
   * WOSH n'opère qu'en Belgique : le pays est affiché, pas choisi. On rend
   * la valeur de l'état plutôt qu'une constante, pour ne pas réécrire en
   * silence une adresse déjà enregistrée sous un autre pays.
   */
  renderCountry(t) {
    return (
      <div className="wosh-ob__span">
        <span className="wosh-ob__label">{t("country")}</span>
        <div className="wosh-ob__field wosh-ob__field--static">
          {this.state.country}
        </div>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const { loading, success, error, errorMessage } = this.state;
    const submitting = loading && !success;

    if (error) {
      return (
        <OnboardingLayout onBack={() => this.setState({ error: false })}>
          <h1 className="wosh-ob__title wosh-ob__centered">
            {t("Woops... It seems Wosh isn't in your area yet...")}
          </h1>
          <p className="wosh-ob__lede wosh-ob__centered">
            {t("Allow us to send you an email when we're in your region")}
          </p>
          <div className="wosh-ob__cta-wrap" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="wosh-ob__cta"
              onClick={() => this.setState({ error: false })}
            >
              {t("Back")}
            </button>
          </div>
        </OnboardingLayout>
      );
    }

    return (
      <OnboardingLayout
        onBack={this.handleBack}
        steps={this.onboarding ? { current: 4, total: 5 } : null}
      >
        <div className="wosh-ob__form">
          <div className="wosh-ob__eyebrow">{t("At home")}</div>
          <h1 className="wosh-ob__title">{t("Pickup and delivery address")}</h1>
          <p className="wosh-ob__lede">
            {t("We collect and deliver at this address.")}
          </p>

          {errorMessage && <p className="wosh-ob__error">{errorMessage}</p>}

          <div className="wosh-ob__card">
            {this.renderField("street", {
              label: t("Street"),
              placeholder: "Rue de Loxum",
              icon: "location",
            })}
            {this.renderField("nr", {
              label: t("Nr"),
              placeholder: "35",
            })}

            <div className="wosh-ob__span wosh-ob__pair">
              {this.renderField("zip_code", {
                label: t("Zip code"),
                placeholder: "1000",
              })}
              {this.renderField("city", {
                label: t("city"),
                placeholder: "Bruxelles",
              })}
            </div>

            {this.renderCountry(t)}

            <div className="wosh-ob__span">
              <label className="wosh-ob__label" htmlFor="address-comment">
                {t("Driver instructions")}
                <span className="wosh-ob__optional">{t("Optional")}</span>
              </label>
              <div className="wosh-ob__field wosh-ob__field--area">
                <Icon name="chat" />
                <textarea
                  id="address-comment"
                  name="comment"
                  rows="2"
                  placeholder={t("Entry code, floor, doorbell…")}
                  value={this.state.comment}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="wosh-ob__cta-wrap">
            <button
              type="button"
              className="wosh-ob__cta"
              disabled={!this.isComplete || submitting}
              onClick={() =>
                this.isEditing
                  ? this.handleEdit(this.editable.id)
                  : this.handleSubmit()
              }
            >
              {submitting && <span className="wosh-ob__spinner" />}
              {submitting ? t("processing") : t("Continue")}
            </button>

            {this.isEditing && (
              <button
                type="button"
                className="wosh-ob__cta wosh-ob__cta--danger"
                disabled={submitting}
                onClick={() => this.handleDelete(this.editable.id)}
              >
                {t("Delete Address")}
              </button>
            )}
          </div>
        </div>
      </OnboardingLayout>
    );
  }
}

const mapStateToProps = (state) => ({
  homeadd: state?.auth?.homeAddress,
  offadd: state?.auth?.officeAddress,
  err: state?.auth?.error,
  authUser: state?.auth?.user,
  selectedAddress: state?.auth?.selectedAddress,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps)
)(DeliveryDetail);
